import { createPortal } from "react-dom";
import { useLayoutEffect, useState } from "react";

const MENU_OFFSET = 6;
const VIEWPORT_PAD = 8;
const MENU_WIDTH = 260;
/** Modest estimate so we do not flip above the anchor before measuring. */
const ESTIMATE_HEIGHT = 140;

/**
 * Renders action menus on document.body so backdrop-filter samples the full
 * page (same behaviour as header search / notification panes).
 * Opens below the anchor, right-aligned to the ⋯ (flips above if needed).
 * Stays hidden until position is measured so it does not flash elsewhere.
 */
export default function PortalInteractionMenu({
  show,
  anchorRef,
  menuRef,
  className = "",
  preferAbove = false,
  compact = false,
  minWidth,
  maxWidth,
  children,
}) {
  const [coords, setCoords] = useState(null);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (!show || !anchorRef?.current) {
      setCoords(null);
      setReady(false);
      return undefined;
    }

    setReady(false);

    const updatePosition = ({ finalize } = { finalize: false }) => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const menuEl = menuRef?.current;
      const fallbackWidth = minWidth ?? MENU_WIDTH;
      const widthCap = Math.min(maxWidth ?? 280, window.innerWidth - VIEWPORT_PAD * 2);
      const measuredWidth = menuEl?.offsetWidth || 0;
      const measuredHeight = menuEl?.offsetHeight || 0;
      const measured = measuredWidth || fallbackWidth;
      const menuWidth = compact
        ? Math.min(measured || 120, maxWidth ?? 160)
        : Math.min(Math.max(measured, fallbackWidth), widthCap);
      // Flip only after real measure — large fallbacks caused a banner flash.
      const menuHeight = measuredHeight || ESTIMATE_HEIGHT;
      const canFlip = measuredHeight > 0;

      let top = preferAbove
        ? rect.top - menuHeight - MENU_OFFSET
        : rect.bottom + MENU_OFFSET;

      if (
        canFlip &&
        !preferAbove &&
        top + menuHeight > window.innerHeight - VIEWPORT_PAD
      ) {
        top = Math.max(VIEWPORT_PAD, rect.top - menuHeight - MENU_OFFSET);
      }

      if (canFlip && preferAbove && top < VIEWPORT_PAD) {
        top = Math.min(
          window.innerHeight - menuHeight - VIEWPORT_PAD,
          rect.bottom + MENU_OFFSET
        );
      }

      const right = Math.max(VIEWPORT_PAD, window.innerWidth - rect.right);
      let left = rect.right - menuWidth;
      left = Math.min(
        Math.max(VIEWPORT_PAD, left),
        window.innerWidth - menuWidth - VIEWPORT_PAD
      );

      setCoords({ top, left, right, width: menuWidth, compact });
      if (finalize) {
        setReady(true);
      }
    };

    updatePosition({ finalize: false });

    const raf = requestAnimationFrame(() => {
      updatePosition({ finalize: true });
    });

    const onReposition = () => updatePosition({ finalize: true });
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [show, anchorRef, menuRef, preferAbove, compact, minWidth, maxWidth]);

  useLayoutEffect(() => {
    if (!show || !coords || !menuRef?.current) return;

    const menuStyle = menuRef.current.style;
    menuStyle.setProperty("position", "fixed", "important");
    menuStyle.setProperty("top", `${coords.top}px`, "important");
    menuStyle.setProperty("left", `${coords.left}px`, "important");
    menuStyle.setProperty("right", "auto", "important");
    menuStyle.setProperty("bottom", "auto", "important");
    menuStyle.setProperty("transform", "none", "important");
    /* Above body-portaled modals (1300). 1080 sits under the dialog. */
    menuStyle.setProperty("z-index", "var(--wlj-z-picker, 1350)", "important");
  }, [show, coords, menuRef]);

  if (!show || !coords) {
    return null;
  }

  const baseStyle = coords.compact
    ? {
        position: "fixed",
        top: coords.top,
        /* Prefer left — global CSS forces right: auto !important on portal menus */
        left: coords.left,
        right: "auto",
        bottom: "auto",
        width: "max-content",
        minWidth: minWidth ?? 120,
        maxWidth: maxWidth ?? 160,
        transform: "none",
        display: "block",
        zIndex: "var(--wlj-z-picker, 1350)",
      }
    : {
        position: "fixed",
        top: coords.top,
        left: coords.left,
        right: "auto",
        bottom: "auto",
        width: coords.width,
        maxWidth: "min(280px, calc(100vw - 24px))",
        transform: "none",
        display: "block",
        zIndex: "var(--wlj-z-picker, 1350)",
      };

  return createPortal(
    <div
      ref={menuRef}
      className={`dropdown-menu show wlj-portal-interaction-menu ${className}`.trim()}
      style={{
        ...baseStyle,
        opacity: ready ? 1 : 0,
        visibility: ready ? "visible" : "hidden",
        pointerEvents: ready ? "auto" : "none",
      }}
      aria-hidden={!ready}
    >
      {children}
    </div>,
    document.body
  );
}
